/**
 * Opening Sketch Draw Tool (Layout persona)
 *
 * A line-style draw tool for door/window OPENING_SKETCHes that snaps to the wall
 * sketch. Behaviour:
 *  - Hover a wall → a marker snaps onto the nearest wall edge showing where the
 *    opening would start (snaps to nodes too).
 *  - Click once to drop the start, move the mouse → a rubber-band span previews
 *    the opening along that wall, click again to commit. (In fixed-length mode a
 *    single click drops a fixed-size opening centered at the cursor.)
 *  - Hover an existing opening — its door/window glyph OR its line on the wall
 *    → it highlights (and the draw preview is suppressed); click to select it,
 *    or drag to slide it along its wall — no prior selection required.
 *  - A selected opening shows two gizmo handles: CLICK one to flip (Side =
 *    swing/inset face, Hinge = hinge end), DRAG one to move the opening along
 *    the gizmo's axis (Hinge slides the offset along the wall, Side shifts
 *    the inset across it).
 *  - ESC cancels an in-progress draw.
 *
 * Tool menu (rendered by the shared settings panel — no bespoke UI):
 *  - Type: door | window (select)
 *  - Fixed length (category toggle) → length number input. When off, length is
 *    free-drawn; the toggle only gates the length.
 *  - Ground offset / height number inputs — ALWAYS applied (sill / head).
 *
 * All four values (fixed-length toggle, width, ground offset, height) are
 * per-user settings and are remembered separately per opening kind (door /
 * window). Switching Type reloads that kind's saved set. See
 * `UserSettingsIntegration` for the registered properties + defaults.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { FloorplanToolBase } from './FloorplanToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { CameraMode } from '../../../defines';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from '../../ToolSettings';
import { DragEventData } from '../../BaseTool';
import { Snapshot } from '../ToolUndoRedoManager';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import { PixelHitTester } from '../modules/selection/PixelHitTester';
import { BoxSelectController, BoxSelectMode, BoxRect } from '../modules/selection/BoxSelectController';
import { SphereMarker } from '../../../utils/SphereMarker';
import { OpeningDistanceController } from '../modules/openingGizmos/OpeningDistanceController';
import { OpeningFlipGizmos } from '../modules/openingGizmos/OpeningFlipGizmos';
import { projectMouseToFloor, snapToWallEdge } from './WallSketchSnapEngine';
import { OpeningKind } from 'shared-types';
import {
  OpeningEdgeHit,
  OPENING_EDGE_RADIUS_PX,
  OPENING_NODE_SNAP_RADIUS_PX,
  MIN_OPENING_LENGTH,
  openingEdgeWorldPoint,
  openingClearSpanOnEdge,
  clampOpeningOffsetToClearSpan,
  openingSpanPolyline,
  openingFootprint,
  pickOpeningSketchAtPixel,
  cursorOffsetOnOpeningEdge,
  cursorPerpOnOpeningEdge,
  createOpeningFloatToggleSnapshot,
  loadOpeningSettingsForKind,
  buildOpeningSketchRequest,
  createOpeningToggleSnapshot,
  createOpeningOffsetMoveSnapshot,
  WallHoleRebuildScheduler,
  OpeningFootprintHighlight,
} from '../modules/openingSketch/OpeningSketchShared';
import {
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchInsetProperty,
  getOpeningSketchSwingPositiveProperty,
} from '@pointorama/potree-shared';
import type { ReactiveProperty, PropertyValueData } from '@pointorama/potree-shared';
import {
  getOpeningSketchFixedLengthProperty,
  getOpeningSketchDefaultLengthProperty,
  getOpeningSketchLowerBoundProperty,
  getOpeningSketchUpperBoundProperty,
} from '../../../../modules/Properties/integration/UserSettingsIntegration';
import { t } from '../../../translation/markers';

const PREVIEW_RENDER_ORDER = 999;
/** Snap-marker color. */
const PREVIEW_COLOR = new THREE.Color(0x00e0c0);
/** The span being drawn over the wall edge — a distinct warm color so it stands
 *  out against the (cool) wall-sketch edges. */
const DRAW_LINE_COLOR = new THREE.Color(0xffaa00);
const DRAW_LINE_WIDTH = 6;

interface ToolSettings {
  kind: OpeningKind;
  /** When true, placement uses `length`; when false, length is free-drawn. */
  fixedLength: boolean;
  length: number;
  /** Sill / head heights (m above floor) — always applied. */
  lowerBound: number;
  upperBound: number;
}

/** A point snapped onto a wall edge — see {@link OpeningEdgeHit}. */
type EdgeHit = OpeningEdgeHit;

export class OpeningSketchDrawTool extends FloorplanToolBase {
  readonly name = 'opening_sketch_draw';

  private hitTester: PixelHitTester;
  /** In-memory mirror of the settings for the currently-shown kind. The source
   *  of truth is the per-user, per-kind reactive properties; this is refreshed
   *  from them in the constructor and whenever the kind changes (see
   *  `loadSettingsForKind`). These literals are only the pre-service-ready
   *  seed — the registered properties own the real per-kind defaults. */
  private settings: ToolSettings = {
    kind: OpeningKind.DOOR,
    fixedLength: false,
    length: 0.9,
    lowerBound: 0,
    upperBound: 2.1,
  };

  /** First click of a free-draw, held until the second click finishes it. */
  private pendingStart: EdgeHit | null = null;

  // Overlay visuals (added to uiScene so they draw on top in 2D).
  private overlay: THREE.Group | null = null;
  private hoverMarker: SphereMarker | null = null;
  private spanLine: Line2 | null = null;
  private spanGeometry: LineGeometry | null = null;
  private spanMaterial: LineMaterial | null = null;
  // Edge highlight drawn over the SELECTED openings (and the one being slid);
  // a single LineSegments2 accumulates every selected footprint so any number
  // of openings highlight at once.
  private highlight = new OpeningFootprintHighlight(this.viewer, () => this.ensureOverlay());

  /** Rubber-band box select over openings. Armed on a mousedown that hits
   *  nothing (no gizmo, no opening, no wall) and started once the drag
   *  threshold confirms it's a drag, mirroring the wall editor. */
  private boxSelect: BoxSelectController | null = null;
  private boxSelectArmed = false;

  // Flip gizmos for the SELECTED opening: Side (which wall face the swing/inset
  // is on) and Hinge (which jamb the door hinges at). They replace the hidden
  // panel fields — click to flip, DRAG to move the opening along the gizmo's
  // axis (Hinge slides the offset along the wall, Side shifts the inset across
  // it). Shown ONLY while the opening is selected. Shared with the Design
  // tab's selection tool via OpeningFlipGizmos.
  private flipGizmos: OpeningFlipGizmos;

  /** Radial menu + "measure distance to element" op for the selected opening. */
  private distanceController: OpeningDistanceController | null = null;

  // Live wall-hole rebuild while sliding an opening — rAF-coalesced so many
  // per-frame mousemoves collapse into one Manifold CSG pass per frame.
  private holeRebuild = new WallHoleRebuildScheduler(() => this.getWallSketch());

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.hitTester = new PixelHitTester(viewer);
    this.flipGizmos = new OpeningFlipGizmos({
      viewer,
      hitTester: this.hitTester,
      ensureOverlay: () => this.ensureOverlay(),
      undoRedo: () => this.undoRedo ?? null,
      renderOrder: PREVIEW_RENDER_ORDER,
    });
    // Opt in to undo/redo so the toolbar undo/redo buttons drive create + move.
    this.initUndoRedo();
    // Prime the in-memory settings from the current kind's persisted user values.
    this.loadSettingsForKind(this.settings.kind);
  }

  // ─── Per-user, per-kind settings persistence ─────────────────────────

  /**
   * Refresh `this.settings` for `kind` from its reactive USER_SETTINGS
   * properties. Each property owns its kind-appropriate default (see
   * `UserSettingsIntegration`), so `.value` already returns the persisted value
   * or that default — no local fallback tables needed here. If the global
   * property service isn't ready yet, the previous `this.settings` is kept.
   * `kind` becomes the currently-shown set.
   */
  private loadSettingsForKind(kind: OpeningKind): void {
    this.settings = loadOpeningSettingsForKind(kind, this.settings);
  }

  /** Persist one setting value for the currently-shown kind. */
  private persistSetting(getter: (kind: string) => ReactiveProperty<PropertyValueData>, value: boolean | number): void {
    try {
      getter(this.settings.kind).value = value;
    } catch {
      /* property service not ready — skip persistence */
    }
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.PLAN_2D_SKELETON.id,
    };
  }

  getSettingsSchema(): ToolSettingsSchema {
    return {
      kind: {
        type: 'select',
        label: t('Type'),
        description: 'Door or window',
        defaultValue: this.settings.kind,
        options: [
          { label: t('Door'), value: OpeningKind.DOOR },
          { label: t('Window'), value: OpeningKind.WINDOW },
        ],
      },
      // Toggle gates ONLY the length. Bounds below always apply.
      fixedLength: {
        type: 'category',
        label: t('Fixed length'),
        description: 'Use a fixed length instead of free-drawing',
        defaultValue: this.settings.fixedLength,
      },
      length: {
        type: 'number',
        label: t('Width'),
        description: 'Opening width along the wall',
        defaultValue: this.settings.length,
        min: 0.1,
        max: 10,
        step: 0.05,
        unit: 'm',
        group: 'fixedLength',
      },
      // Bounds are ungrouped: they're always applied, regardless of the toggle.
      lowerBound: {
        type: 'number',
        label: t('Ground offset'),
        description: 'Sill height above the floor',
        defaultValue: this.settings.lowerBound,
        min: 0,
        max: 10,
        step: 0.05,
        unit: 'm',
      },
      upperBound: {
        type: 'number',
        label: t('Height'),
        description: 'Head height above the floor',
        defaultValue: this.settings.upperBound,
        min: 0,
        max: 10,
        step: 0.05,
        unit: 'm',
      },
    };
  }

  onSettingsChange(event: ToolSettingsChangeEvent): void {
    switch (event.key) {
      case 'kind':
        // Switch the shown set to the other kind's saved values, then rebuild
        // the panel so length / bounds / toggle reflect that kind.
        this.loadSettingsForKind(event.value === OpeningKind.WINDOW ? OpeningKind.WINDOW : OpeningKind.DOOR);
        this.getSettingsPanel()?.rebuild(this.getSettingsSchema());
        break;
      case 'fixedLength':
        this.settings.fixedLength = Boolean(event.value);
        this.persistSetting(getOpeningSketchFixedLengthProperty, this.settings.fixedLength);
        break;
      case 'length':
        this.settings.length = Number(event.value);
        this.persistSetting(getOpeningSketchDefaultLengthProperty, this.settings.length);
        break;
      case 'lowerBound':
        this.settings.lowerBound = Number(event.value);
        this.persistSetting(getOpeningSketchLowerBoundProperty, this.settings.lowerBound);
        break;
      case 'upperBound':
        this.settings.upperBound = Number(event.value);
        this.persistSetting(getOpeningSketchUpperBoundProperty, this.settings.upperBound);
        break;
    }
  }

  protected onEnable(): void {
    super.onEnable();
    this.viewer.renderer.domElement.style.cursor = 'crosshair';
    this.pendingStart = null;
    this.ensureOverlay();
    this.viewer.renderer.domElement.addEventListener('mousemove', this.handleHoverMove);
    // Per-frame refresh so the SELECTED opening's edge highlight + flip gizmos
    // appear immediately on select (no mouse move needed) and follow the camera.
    this.viewer.addEventListener('update', this.boundRefreshSelectionOverlay);
  }

  protected onDisable(): void {
    this.viewer.renderer.domElement.style.cursor = '';
    this.viewer.renderer.domElement.removeEventListener('mousemove', this.handleHoverMove);
    this.viewer.removeEventListener('update', this.boundRefreshSelectionOverlay);
    // Safety: never leave selection suppressed if the tool is torn down mid-flip.
    this.viewer.inputHandler.selectionDisabled = false;
    this.distanceController?.dispose();
    this.distanceController = null;
    this.pendingStart = null;
    this.handleDrag = null;
    this.openingDrag = null;
    this.drawDrag = null;
    if (this.boxSelect?.isActive()) this.viewer.inputHandler.unblockOrbitControls();
    this.boxSelect?.dispose();
    this.boxSelect = null;
    this.boxSelectArmed = false;
    if (this.dragOrbitBlocked) {
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
    }
    this.cancelWallHoleRebuild();
    this.disposeOverlay();
    super.onDisable();
  }

  private getWallSketch(): PointoramaWallSketch | null {
    if (!this.currentFloorGroupIdentifier) return null;
    return this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === this.currentFloorGroupIdentifier) || null;
  }

  /** Lazily build the distance-measure radial-menu controller. */
  private ensureDistanceController(): OpeningDistanceController {
    if (!this.distanceController) {
      this.distanceController = new OpeningDistanceController({
        viewer: this.viewer,
        hitTester: this.hitTester,
        undoRedo: () => this.undoRedo ?? null,
        getWallSketch: () => this.getWallSketch(),
        openingFootprint: (skeleton, openingId) => this.openingFootprint(skeleton, openingId),
        rebuildWallHole: () => this.getWallSketch()?.updateSkinningVisualization(),
      });
    }
    return this.distanceController;
  }

  // ─── Snapping ────────────────────────────────────────────────────────

  /**
   * Snap the cursor to the wall sketch: prefer a nearby node, else the nearest
   * point on the nearest edge. Returns the edge + along-wall offset + world point,
   * or null when not near any wall. (Shared logic lives in `snapToWallEdge`.)
   */
  private snapToWall(event: MouseEvent, skeleton: PointoramaWallSketch): EdgeHit | null {
    return snapToWallEdge(event, skeleton, this.hitTester, this.viewer.scene.getActiveCamera(), this.viewer.renderer.domElement, {
      edgeRadiusPx: OPENING_EDGE_RADIUS_PX,
      nodeSnapRadiusPx: OPENING_NODE_SNAP_RADIUS_PX,
    });
  }

  private edgeWorldPoint(skeleton: PointoramaWallSketch, edgeId: string, offset: number): THREE.Vector3 | null {
    return openingEdgeWorldPoint(skeleton, edgeId, offset);
  }

  private spanPolyline(skeleton: PointoramaWallSketch, edgeId: string, s0: number, s1: number): THREE.Vector3[] | null {
    return openingSpanPolyline(skeleton, edgeId, s0, s1);
  }

  private openingFootprint(skeleton: PointoramaWallSketch, openingId: string): { a: THREE.Vector3; b: THREE.Vector3; pts: THREE.Vector3[] } | null {
    return openingFootprint(skeleton, openingId);
  }

  private pickOpeningAtPixel(mousePx: THREE.Vector2, skeleton: PointoramaWallSketch): string | null {
    return pickOpeningSketchAtPixel(this.viewer, skeleton, mousePx, this.hitTester);
  }

  // ─── Box select ──────────────────────────────────────────────────────

  private ensureBoxSelect(): BoxSelectController {
    if (!this.boxSelect) {
      this.boxSelect = new BoxSelectController(this.viewer, {
        boxStyle: {
          border: '1.5px dashed #00ddff',
          background: 'rgba(0, 221, 255, 0.08)',
        },
      });
    }
    return this.boxSelect;
  }

  /** Openings whose footprint polyline crosses the box go into the Redux
   *  selection with the drag's mode (replace / Shift-add / Ctrl-remove). An
   *  empty `replace` box clears the selection, like every marquee. */
  private commitBoxSelect(mode: BoxSelectMode, rect: BoxRect): void {
    const skeleton = this.getWallSketch();
    if (!skeleton) return;
    const identifiers: string[] = [];
    for (const opening of this.viewer.scene.openingSketches) {
      if (opening.groupIdentifier !== skeleton.groupIdentifier) continue;
      const footprint = this.openingFootprint(skeleton, opening.identifier);
      if (!footprint) continue;
      let prev: THREE.Vector2 | null = null;
      let inside = false;
      for (const p of footprint.pts) {
        const px = this.hitTester.projectWorldToPixel(p);
        if (!px) {
          prev = null;
          continue;
        }
        if (prev && PixelHitTester.segmentIntersectsRect(prev, px, rect.minX, rect.minY, rect.maxX, rect.maxY)) {
          inside = true;
          break;
        }
        prev = px;
      }
      if (inside) identifiers.push(opening.identifier);
    }
    this.viewer.scene.dispatchEvent({ type: 'annotations_box_select', value: { identifiers, mode } });
  }

  // ─── Side / Hinge toggle handles ─────────────────────────────────────
  // (geometry, picking and flipping live in OpeningFlipGizmos — shared with
  // the Design tab's selection tool)

  /** Undo snapshot for a float property change: toggles between before/after. */
  private createFloatToggleSnapshot(getter: (id: string) => ReactiveProperty<number>, openingId: string, before: number, after: number): Snapshot {
    return createOpeningFloatToggleSnapshot(getter, openingId, before, after);
  }

  /** The currently-selected opening sketches on this floor. Selection is owned
   *  by the scene objects (`os.selected`, mirrored from Redux); the edge
   *  highlight stays visible for them even when the cursor isn't over them. */
  private getSelectedOpeningIds(skeleton: PointoramaWallSketch): string[] {
    const ids: string[] = [];
    for (const os of this.viewer.scene.openingSketches) {
      if (os.groupIdentifier === skeleton.groupIdentifier && os.selected) ids.push(os.identifier);
    }
    return ids;
  }

  /** Per-frame: show + position the SELECTED openings' edge highlight + flip
   *  gizmos. Runs every frame so they appear immediately on select (no mouse
   *  move needed) and track the camera on scroll/zoom/pan. The highlight covers
   *  every selected opening; the flip gizmos (and the radial menu) only apply
   *  to a SINGLE selected opening — a multi-selection hides them. */
  private boundRefreshSelectionOverlay = (): void => {
    if (this.pendingStart) return; // mid free-draw — don't compete with the preview
    const skeleton = this.getWallSketch();
    if (!skeleton) return;
    const selectedIds = this.getSelectedOpeningIds(skeleton);
    if (selectedIds.length !== 1) {
      if (this.flipGizmos.currentOpeningId) this.flipGizmos.hide();
      // No single selection → tear down the radial menu (it belongs to one
      // opening), UNLESS it's mid pick/input: that flow drives selection itself
      // and a transient deselect shouldn't kill the in-progress measurement.
      if (this.distanceController?.isActive() && !this.distanceController.isModal()) this.distanceController.cancel();
    }
    if (selectedIds.length === 0) {
      this.hideHighlight();
      return;
    }
    const polylines: THREE.Vector3[][] = [];
    for (const id of selectedIds) {
      const footprint = this.openingFootprint(skeleton, id);
      if (footprint) polylines.push(footprint.pts);
    }
    this.showHighlight(polylines);
    if (selectedIds.length === 1 && polylines.length > 0) this.flipGizmos.show(skeleton, selectedIds[0]);
  };

  // ─── Hover feedback ──────────────────────────────────────────────────

  private handleHoverMove = (event: MouseEvent): void => {
    // Mid box select the rubber-band owns the interaction — no hover preview.
    if (this.boxSelect?.isActive()) return;
    // During a distance-measure pick, route hover to the controller (target
    // highlight + cursor) and suppress the normal draw/hover preview.
    if (this.distanceController?.isModal()) {
      this.distanceController.onMouseMove(event);
      this.hideMarker();
      this.hideSpan();
      return;
    }
    const skeleton = this.getWallSketch();
    if (!skeleton) {
      this.hideMarker();
      this.hideSpan();
      return;
    }

    // The flip gizmos + edge highlight belong to the SELECTED opening only (see
    // boundRefreshSelectionOverlay, which shows/positions them every frame). They
    // are clickable while shown, so hovering one gets the pointer cursor.
    const mousePx = this.hitTester.mousePx(event);
    const overHandle = this.flipGizmos.currentOpeningId != null && this.flipGizmos.pickAtPixel(mousePx) !== null;
    if (overHandle) {
      this.hideMarker();
      this.hideSpan();
      this.viewer.renderer.domElement.style.cursor = 'pointer';
      return;
    }

    // Hovering an existing opening shows a move cursor (a click selects it, a
    // drag slides it) and suppresses the new-draw preview. Skip while mid
    // free-draw so an in-progress span isn't hijacked by passing over an opening.
    const hoverOpeningId = this.pendingStart ? null : this.pickOpeningAtPixel(mousePx, skeleton);
    if (hoverOpeningId) {
      this.hideMarker();
      this.hideSpan();
      this.viewer.renderer.domElement.style.cursor = 'move';
      return;
    }

    // Pointer over a wall (a click places an opening there); crosshair over
    // empty space where a click does nothing.
    const hit = this.snapToWall(event, skeleton);
    this.viewer.renderer.domElement.style.cursor = hit ? 'pointer' : 'crosshair';

    if (!hit) {
      this.hideMarker();
      this.hideSpan();
      return;
    }

    this.showMarker(hit.point);

    // While drawing free-length, rubber-band the span from the start to the
    // cursor (both ends held inside the corner-clear span, like the commit).
    if (this.pendingStart && this.pendingStart.edgeId === hit.edgeId && !this.settings.fixedLength) {
      const span = openingClearSpanOnEdge(skeleton, this.pendingStart.edgeId);
      const s0 = Math.max(span.start, Math.min(span.end, this.pendingStart.offset));
      const s1 = Math.max(span.start, Math.min(span.end, hit.offset));
      const pts = this.spanPolyline(skeleton, this.pendingStart.edgeId, s0, s1);
      if (pts) this.showSpan(pts);
    } else if (this.settings.fixedLength) {
      // Preview the fixed-length footprint centered on the cursor.
      const lo = clampOpeningOffsetToClearSpan(skeleton, hit.edgeId, this.settings.length, hit.offset - this.settings.length / 2);
      const pts = this.spanPolyline(skeleton, hit.edgeId, lo, lo + this.settings.length);
      if (pts) this.showSpan(pts);
    } else {
      this.hideSpan();
    }
  };

  // ─── Drawing ─────────────────────────────────────────────────────────

  public onClick(event: MouseEvent): void {
    if (event.button === 1) return; // middle-click = camera pan
    // While the distance controller is picking/inputting, its mousedown already
    // consumed the interaction — don't select/draw on the trailing click.
    if (this.distanceController?.isModal()) return;
    // A gizmo handle armed on mousedown consumes this interaction either way:
    // a plain click (no drag) performs the FLIP now, on release; the trailing
    // click of a drag-move is just swallowed. Restore the selection
    // suppression set when the handle was armed.
    if (this.handleDrag) {
      const drag = this.handleDrag;
      this.handleDrag = null;
      this.viewer.inputHandler.selectionDisabled = false;
      if (!drag.moved) {
        this.flipGizmos.flip(drag.handle, drag.openingId);
        // Reposition the handles to follow the flipped state immediately.
        const skeleton = this.getWallSketch();
        if (skeleton) this.flipGizmos.show(skeleton, drag.openingId);
      }
      return;
    }
    const skeleton = this.getWallSketch();
    if (!skeleton) return;

    // Click an existing opening (its glyph or its line on the wall) → select
    // it (don't start a draw). Ctrl/Cmd/Shift all accumulate a multi-selection.
    const mousePx = this.hitTester.mousePx(event);
    const clickedOpeningId = this.pickOpeningAtPixel(mousePx, skeleton);
    if (clickedOpeningId) {
      const additive = event.ctrlKey || event.metaKey || event.shiftKey;
      this.selectOpeningSketch(clickedOpeningId, additive);
      this.pendingStart = null;
      this.hideSpan();
      // Pop the radial menu next to the selected opening (skip on multi-select).
      if (!additive) {
        const anchor = projectMouseToFloor(event, this.viewer.renderer.domElement, this.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
        if (anchor) this.ensureDistanceController().openMenuForSelection(clickedOpeningId, anchor);
      }
      return;
    }

    const hit = this.snapToWall(event, skeleton);
    if (!hit) {
      this.pendingStart = null;
      this.hideSpan();
      return;
    }

    // Fixed-length mode: a single click drops a fixed-length opening.
    if (this.settings.fixedLength) {
      const offset = clampOpeningOffsetToClearSpan(skeleton, hit.edgeId, this.settings.length, hit.offset - this.settings.length / 2);
      this.createOpening(skeleton, hit.edgeId, offset, this.settings.length);
      return;
    }

    // Free-draw: first click sets start, second click (same wall) sets end.
    if (!this.pendingStart) {
      this.pendingStart = hit;
      return;
    }
    if (hit.edgeId !== this.pendingStart.edgeId) {
      this.pendingStart = hit; // restart on the new wall
      return;
    }
    // Both ends held inside the corner-clear span — a click on/past a corner
    // node lands the opening flush with the crossing wall's inner face.
    const span = openingClearSpanOnEdge(skeleton, hit.edgeId);
    const lo = Math.max(span.start, Math.min(this.pendingStart.offset, hit.offset));
    const hi = Math.min(span.end, Math.max(this.pendingStart.offset, hit.offset));
    const length = hi - lo;
    this.pendingStart = null;
    this.hideSpan();
    if (length < MIN_OPENING_LENGTH) return;
    this.createOpening(skeleton, hit.edgeId, lo, length);
  }

  private createOpening(skeleton: PointoramaWallSketch, edgeId: string, offset: number, length: number): void {
    // Bounds always come from the (per-kind, persisted) settings; the request
    // builder converts the floor-relative sill/head heights to the stored
    // absolute-lower + relative-height pair and mints the identifier (so the
    // undo snapshot can re-create the exact same opening on redo).
    const request = buildOpeningSketchRequest(skeleton, {
      edgeId,
      offset,
      length,
      kind: this.settings.kind,
      lowerBound: this.settings.lowerBound,
      upperBound: this.settings.upperBound,
    });
    if (!request) return;
    this.viewer.scene.dispatchEvent({ type: 'request_opening_sketch', value: request });
    this.undoRedo?.pushState(createOpeningToggleSnapshot(this.viewer, request, true));
  }

  /** Undo snapshot for a move (offset change): toggles between before/after. */
  private createMoveSnapshot(openingId: string, before: number, after: number): Snapshot {
    return createOpeningOffsetMoveSnapshot(() => this.getWallSketch(), openingId, before, after);
  }

  private selectOpeningSketch(openingSketchId: string, isCtrl: boolean): void {
    const openingSketch = this.viewer.scene.openingSketches.find((o) => o.identifier === openingSketchId);
    if (!openingSketch) return;
    this.viewer.scene.dispatchEvent({
      type: 'measurement_select',
      value: { measure: openingSketch, isCtrl },
    });
  }

  public onKeyDown(event: KeyboardEvent): void {
    // Let the distance controller handle Escape (cancel pick/input) first.
    if (this.distanceController?.onKeyDown(event)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.key === 'Escape' && this.boxSelect?.isActive()) {
      event.preventDefault();
      event.stopPropagation();
      this.boxSelect.cancel();
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
      return;
    }
    if (event.key === 'Escape' && this.pendingStart) {
      event.preventDefault();
      event.stopPropagation();
      this.pendingStart = null;
      this.hideSpan();
    }
  }

  /** Whether onDragStart blocked the orbit controls for an opening
   *  interaction drag (slide/draw/handle) — released in onDragEnd / onDisable. */
  private dragOrbitBlocked = false;
  // Drag an existing opening along its wall (slide). Grab is captured on
  // mousedown so it registers before the drag threshold.
  private openingDrag: { openingId: string; edgeId: string; grabDelta: number; beforeOffset: number } | null = null;
  // Drag-to-draw a new opening: start captured on mousedown over empty wall,
  // committed on drag-end. (A plain click without a real drag falls through to
  // the click-click flow in onClick.)
  private drawDrag: EdgeHit | null = null;
  /** Mousedown-armed interaction on a flip gizmo. A plain click (no drag)
   *  performs the flip on release, exactly as before; DRAGGING moves the
   *  opening along the gizmo's axis instead — the Hinge gizmo (along the
   *  wall) slides the `offset`, the Side gizmo (across the wall) shifts the
   *  `inset`. */
  private handleDrag: {
    openingId: string;
    handle: 'side' | 'hinge';
    edgeId: string;
    beforeOffset: number;
    /** Cursor's along-wall offset minus `beforeOffset` at grab time. */
    grabDelta: number;
    beforeInset: number;
    /** Cursor's signed perpendicular off the centerline at grab time. */
    grabPerp: number;
    /** Side the inset is measured from at grab time (perp→inset sign). */
    swingPositive: boolean;
    moved: boolean;
  } | null = null;

  public onMouseDown(event: MouseEvent): void {
    if (event.button === 1) return;
    this.armPressInteractions(event);
    // A press that armed a tool interaction can only become a tool gesture —
    // block orbit RIGHT NOW rather than at the drag threshold: waiting loses
    // the race against the camera controls, which rotate during the first
    // pixels and keep drifting on their damped deltas (same reasoning as the
    // wall editor's empty-press orbit block). Released in onMouseUp (which
    // fires after onDragEnd on every release) or onDisable.
    if (event.button === 0 && (this.boxSelectArmed || this.openingDrag || this.drawDrag || this.handleDrag)) {
      this.dragOrbitBlocked = true;
      this.viewer.inputHandler.blockOrbitControls();
    }
  }

  /** Arm whichever press interaction the mousedown hits (gizmo handle,
   *  opening slide, drag-to-draw start, or the empty-space box select) —
   *  split out so onMouseDown can apply the orbit block after ANY arming
   *  branch returned. */
  private armPressInteractions(event: MouseEvent): void {
    // The distance-measure controller (radial menu pick phase) takes priority —
    // a click there picks the target, not a slide/draw/select.
    if (this.distanceController?.onMouseDown(event)) return;
    this.openingDrag = null;
    this.drawDrag = null;
    this.handleDrag = null;
    this.boxSelectArmed = false;
    const skeleton = this.getWallSketch();
    if (!skeleton) return;

    const mousePx = this.hitTester.mousePx(event);

    // A gizmo handle (Side / Hinge) takes priority over slide/draw/select — it
    // sits on the currently-hovered opening. ARM the interaction: the flip is
    // performed on the trailing click (no drag), while a real drag moves the
    // opening along the gizmo's axis (see onDrag).
    if (this.flipGizmos.currentOpeningId) {
      const handle = this.flipGizmos.pickAtPixel(mousePx);
      if (handle) {
        try {
          const openingId = this.flipGizmos.currentOpeningId;
          const edgeId = getOpeningSketchWallEdgeIdProperty(openingId).value || '';
          if (!edgeId) return;
          const cursorAlong = this.snapPointOffset(event, skeleton, edgeId);
          const cursorPerp = this.cursorPerpOnEdge(event, skeleton, edgeId);
          if (cursorAlong === null || cursorPerp === null) return;
          const beforeOffset = getOpeningSketchOffsetProperty(openingId).value ?? 0;
          this.handleDrag = {
            openingId,
            handle,
            edgeId,
            beforeOffset,
            grabDelta: cursorAlong - beforeOffset,
            beforeInset: getOpeningSketchInsetProperty(openingId).value ?? 0,
            grabPerp: cursorPerp,
            swingPositive: getOpeningSketchSwingPositiveProperty(openingId).value,
            moved: false,
          };
          // The gizmos sit OFF the wall, so the InputHandler's parallel click-
          // selection would pick empty space here and DESELECT the opening.
          // Suppress it for this interaction; restored in onClick / onDragEnd.
          this.viewer.inputHandler.selectionDisabled = true;
        } catch {
          /* property service not ready */
        }
        return;
      }
    }

    // On an existing opening (its glyph OR its line on the wall) → prepare to
    // SLIDE it. No prior selection required.
    const openingId = this.pickOpeningAtPixel(mousePx, skeleton);
    if (openingId) {
      let edgeId = '';
      let currentOffset = 0;
      try {
        edgeId = getOpeningSketchWallEdgeIdProperty(openingId).value || '';
        currentOffset = getOpeningSketchOffsetProperty(openingId).value ?? 0;
      } catch {
        return;
      }
      if (!edgeId) return;
      const cursor = this.snapPointOffset(event, skeleton, edgeId);
      if (cursor === null) return;
      this.pendingStart = null;
      this.openingDrag = { openingId, edgeId, grabDelta: cursor - currentOffset, beforeOffset: currentOffset };
      return;
    }

    // On empty wall → prepare to DRAW by dragging. Free-draw only; fixed-
    // length placement is a single click (handled in onClick).
    const hit = this.snapToWall(event, skeleton);
    if (hit) {
      if (!this.settings.fixedLength) this.drawDrag = hit;
      return;
    }

    // Off the walls entirely → a left-drag from here is a rubber-band box
    // select over the openings (the AreaSelector only appears once the drag
    // threshold confirms it's a drag, see onDragStart). Suppressed mid
    // free-draw so an in-progress span isn't hijacked. 2D ortho views only —
    // in 3D a left-drag is orbit navigation (same restriction as the wall
    // editor's isInteractiveViewActive).
    if (event.button === 0 && !this.pendingStart && this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
      this.boxSelectArmed = true;
    }
  }

  /** Cursor's along-wall offset on a specific edge (no node snapping — for dragging). */
  private snapPointOffset(event: MouseEvent, skeleton: PointoramaWallSketch, edgeId: string): number | null {
    return cursorOffsetOnOpeningEdge(event, this.viewer, skeleton, edgeId);
  }

  /** Cursor's SIGNED perpendicular distance off the edge centerline — see
   *  {@link cursorPerpOnOpeningEdge} (shared with the flip-gizmo drag). */
  private cursorPerpOnEdge(event: MouseEvent, skeleton: PointoramaWallSketch, edgeId: string): number | null {
    return cursorPerpOnOpeningEdge(event, this.viewer, skeleton, edgeId);
  }

  private scheduleWallHoleRebuild(): void {
    this.holeRebuild.schedule();
  }

  private cancelWallHoleRebuild(): void {
    this.holeRebuild.cancel();
  }

  /** The drag threshold was just crossed — if the mousedown armed a box
   *  select (hit nothing), start the rubber-band now. Starting here rather
   *  than on mousedown keeps sub-threshold clicks from flashing a phantom
   *  rectangle (same reasoning as the door/window tool). */
  public onDragStart(data: DragEventData): void {
    if (!this.boxSelectArmed || data.button !== 0) return;
    this.boxSelectArmed = false;
    this.ensureBoxSelect().start(data.event);
  }

  /** Release the press-time orbit block (see onMouseDown) once the gesture
   *  resolves — fires on every left release, drag and plain click alike
   *  (after onDragEnd committed a real drag). */
  public onMouseUp(event: MouseEvent): void {
    if (event.button !== 0) return;
    if (this.dragOrbitBlocked) {
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
    }
  }

  public onDrag(data: DragEventData): void {
    if (data.button === 1) return;
    if (this.boxSelect?.isActive()) {
      this.boxSelect.update(data.event);
      return;
    }
    const skeleton = this.getWallSketch();
    if (!skeleton) return;

    // Dragging a gizmo handle MOVES the opening along the gizmo's axis:
    // Hinge (along the wall) slides the offset, Side (across the wall)
    // shifts the inset. A plain click (never reaching onDrag) still flips.
    if (this.handleDrag) {
      const drag = this.handleDrag;
      if (drag.handle === 'hinge') {
        const cursor = this.snapPointOffset(data.event, skeleton, drag.edgeId);
        if (cursor === null) return;
        let length = 0;
        try {
          length = getOpeningSketchLengthProperty(drag.openingId).value ?? 0;
        } catch {
          return;
        }
        const clamped = clampOpeningOffsetToClearSpan(skeleton, drag.edgeId, length, cursor - drag.grabDelta);
        try {
          getOpeningSketchOffsetProperty(drag.openingId).value = clamped;
        } catch {
          return;
        }
        // Follow the slide like the body-grab slide: highlight + live hole.
        const footprint = this.openingFootprint(skeleton, drag.openingId);
        if (footprint) this.showHighlight([footprint.pts]);
        this.scheduleWallHoleRebuild();
      } else {
        const perp = this.cursorPerpOnEdge(data.event, skeleton, drag.edgeId);
        if (perp === null) return;
        // Inset grows INWARD from the outer face on the swing side, so a perp
        // move maps to inset with the side's sign; clamped to the wall
        // thickness (glyph stays between the two faces).
        const deltaPerp = perp - drag.grabPerp;
        const rawInset = drag.beforeInset + (drag.swingPositive ? -deltaPerp : deltaPerp);
        const edge = skeleton.getEdge(drag.edgeId);
        const thickness = (edge?.leftSize ?? 0.1) + (edge?.rightSize ?? 0.1);
        try {
          getOpeningSketchInsetProperty(drag.openingId).value = Math.max(0, Math.min(thickness, rawInset));
        } catch {
          return;
        }
      }
      drag.moved = true;
      return;
    }

    // Slide an existing opening.
    if (this.openingDrag) {
      const cursor = this.snapPointOffset(data.event, skeleton, this.openingDrag.edgeId);
      if (cursor === null) return;
      let length = 0;
      try {
        length = getOpeningSketchLengthProperty(this.openingDrag.openingId).value ?? 0;
      } catch {
        return;
      }
      const clamped = clampOpeningOffsetToClearSpan(skeleton, this.openingDrag.edgeId, length, cursor - this.openingDrag.grabDelta);
      try {
        getOpeningSketchOffsetProperty(this.openingDrag.openingId).value = clamped;
      } catch {
        // property service not ready — ignore
      }
      // Keep the highlight on the opening as it slides to its new footprint.
      const footprint = this.openingFootprint(skeleton, this.openingDrag.openingId);
      if (footprint) this.showHighlight([footprint.pts]);
      // Move the wall hole live (rAF-throttled) — the offset write above only
      // patched the glyph; this re-cuts the hole to follow the slide.
      this.scheduleWallHoleRebuild();
      return;
    }

    // Draw a new opening by dragging — live preview span on the start edge
    // (both ends held inside the corner-clear span, like the commit).
    if (this.drawDrag) {
      const end = this.snapPointOffset(data.event, skeleton, this.drawDrag.edgeId);
      if (end === null) return;
      const endPt = this.edgeWorldPoint(skeleton, this.drawDrag.edgeId, end);
      this.showMarker(endPt ?? this.drawDrag.point);
      const span = openingClearSpanOnEdge(skeleton, this.drawDrag.edgeId);
      const s0 = Math.max(span.start, Math.min(span.end, this.drawDrag.offset));
      const s1 = Math.max(span.start, Math.min(span.end, end));
      const pts = this.spanPolyline(skeleton, this.drawDrag.edgeId, s0, s1);
      if (pts) this.showSpan(pts);
    }
  }

  public onDragEnd(data: DragEventData): void {
    if (data.button === 1) return;

    // Commit a box select: hit-test every opening on this floor against the
    // rectangle and apply the captured mode to the Redux selection. (The
    // orbit block is released by onMouseUp, which fires right after.)
    if (this.boxSelect?.isActive()) {
      const result = this.boxSelect.finish();
      if (result) this.commitBoxSelect(result.mode, result.rect);
      return;
    }

    // Finish a gizmo-handle drag: commit the moved offset/inset with an undo
    // snapshot. The state is kept until the trailing click is swallowed in
    // onClick (so the release doesn't select/draw/flip), then cleared as a
    // fallback.
    if (this.handleDrag) {
      const drag = this.handleDrag;
      this.viewer.inputHandler.selectionDisabled = false;
      // A real drag consumed the interaction even if nothing moved (e.g. the
      // cursor projection failed) — the trailing click must NOT flip.
      const moved = drag.moved;
      drag.moved = true;
      setTimeout(() => {
        this.handleDrag = null;
      }, 0);
      if (!moved) return;
      if (drag.handle === 'hinge') {
        this.cancelWallHoleRebuild();
        let afterOffset = drag.beforeOffset;
        try {
          afterOffset = getOpeningSketchOffsetProperty(drag.openingId).value ?? drag.beforeOffset;
        } catch {
          /* property service not ready */
        }
        if (Math.abs(afterOffset - drag.beforeOffset) > 1e-6) {
          this.undoRedo?.pushState(this.createMoveSnapshot(drag.openingId, drag.beforeOffset, afterOffset));
          // Final authoritative hole re-cut at the settled position.
          this.getWallSketch()?.updateSkinningVisualization();
        }
      } else {
        let afterInset = drag.beforeInset;
        try {
          afterInset = getOpeningSketchInsetProperty(drag.openingId).value ?? drag.beforeInset;
        } catch {
          /* property service not ready */
        }
        if (Math.abs(afterInset - drag.beforeInset) > 1e-6) {
          this.undoRedo?.pushState(this.createFloatToggleSnapshot(getOpeningSketchInsetProperty, drag.openingId, drag.beforeInset, afterInset));
        }
      }
      return;
    }

    // Finish a slide: record an undo snapshot if the offset actually moved.
    if (this.openingDrag) {
      // Drop any pending live rebuild — we run one final authoritative pass below.
      this.cancelWallHoleRebuild();
      const drag = this.openingDrag;
      this.openingDrag = null;
      let afterOffset = drag.beforeOffset;
      try {
        afterOffset = getOpeningSketchOffsetProperty(drag.openingId).value ?? drag.beforeOffset;
      } catch {
        /* property service not ready */
      }
      if (Math.abs(afterOffset - drag.beforeOffset) > 1e-6) {
        this.undoRedo?.pushState(this.createMoveSnapshot(drag.openingId, drag.beforeOffset, afterOffset));
        // Final authoritative hole re-cut at the settled position (the live
        // rAF passes during the slide were throttled and may have missed the
        // last frame).
        this.getWallSketch()?.updateSkinningVisualization();
        // Re-file into the adjacent room's folder (handled in usePotreeEvents).
        this.viewer.scene.dispatchEvent({ type: 'opening_sketch_moved', value: { identifier: drag.openingId } });
      }
    }

    // Commit a drag-drawn opening.
    if (this.drawDrag) {
      const skeleton = this.getWallSketch();
      const start = this.drawDrag;
      this.drawDrag = null;
      this.hideSpan();
      if (!skeleton) return;
      const end = this.snapPointOffset(data.event, skeleton, start.edgeId);
      if (end === null) return;
      const span = openingClearSpanOnEdge(skeleton, start.edgeId);
      const lo = Math.max(span.start, Math.min(start.offset, end));
      const hi = Math.min(span.end, Math.max(start.offset, end));
      const length = hi - lo;
      if (length < MIN_OPENING_LENGTH) return;
      this.createOpening(skeleton, start.edgeId, lo, length);
    }
  }

  // ─── Overlay (hover marker + span preview) ───────────────────────────

  private ensureOverlay(): THREE.Group {
    if (!this.overlay) {
      this.overlay = new THREE.Group();
      this.overlay.name = 'opening_sketch_draw_overlay';
      this.viewer.uiScene.add(this.overlay);
    }
    return this.overlay;
  }

  private showMarker(at: THREE.Vector3): void {
    const group = this.ensureOverlay();
    if (!this.hoverMarker) {
      this.hoverMarker = new SphereMarker(this.viewer);
      this.hoverMarker.setColor(PREVIEW_COLOR);
      this.hoverMarker.setScaleFactor(1.2);
      this.hoverMarker.setRenderOrder(PREVIEW_RENDER_ORDER);
      group.add(this.hoverMarker);
    }
    this.hoverMarker.position.set(at.x, at.y, at.z + 0.03);
    this.hoverMarker.visible = true;
  }

  private hideMarker(): void {
    if (this.hoverMarker) this.hoverMarker.visible = false;
  }

  /** Draw the colored span being created, lying ON the wall edge (polyline —
   *  a 2-point segment on straight walls, a tessellated arc on curved ones). */
  private showSpan(pts: THREE.Vector3[]): void {
    if (pts.length < 2) {
      this.hideSpan();
      return;
    }
    const group = this.ensureOverlay();
    // Lift slightly above the floor so the fat line sits over the edge, not in it.
    const positions: number[] = [];
    for (const p of pts) positions.push(p.x, p.y, p.z + 0.04);
    if (!this.spanLine) {
      this.spanGeometry = new LineGeometry();
      this.spanGeometry.setPositions(positions);
      this.spanMaterial = new LineMaterial({
        color: DRAW_LINE_COLOR.getHex(),
        linewidth: DRAW_LINE_WIDTH,
        resolution: new THREE.Vector2(this.viewer.renderer.domElement.clientWidth || 1000, this.viewer.renderer.domElement.clientHeight || 1000),
        depthTest: false,
        transparent: true,
      });
      this.spanLine = new Line2(this.spanGeometry, this.spanMaterial);
      this.spanLine.renderOrder = PREVIEW_RENDER_ORDER;
      this.spanLine.computeLineDistances();
      group.add(this.spanLine);
    } else {
      // LineGeometry's buffers are sized at construction — swap the geometry
      // when the polyline vertex count changes.
      const old = this.spanGeometry;
      this.spanGeometry = new LineGeometry();
      this.spanGeometry.setPositions(positions);
      this.spanLine.geometry = this.spanGeometry;
      old?.dispose();
      this.spanLine.computeLineDistances();
      // Keep the screen-space line width correct if the canvas resized.
      this.spanMaterial?.resolution.set(this.viewer.renderer.domElement.clientWidth || 1000, this.viewer.renderer.domElement.clientHeight || 1000);
    }
    this.spanLine.visible = true;
  }

  private hideSpan(): void {
    if (this.spanLine) this.spanLine.visible = false;
  }

  private showHighlight(polylines: THREE.Vector3[][]): void {
    this.highlight.show(polylines);
  }

  private hideHighlight(): void {
    this.highlight.hide();
  }

  private disposeOverlay(): void {
    if (this.spanLine) {
      this.spanGeometry?.dispose();
      this.spanMaterial?.dispose();
      this.spanLine = null;
      this.spanGeometry = null;
      this.spanMaterial = null;
    }
    this.highlight.dispose();
    this.hoverMarker = null; // SphereMarker children are disposed with the group
    // The flip handles register a viewer 'update' listener — dispose to detach.
    this.flipGizmos.dispose();
    if (this.overlay) {
      this.viewer.uiScene.remove(this.overlay);
      this.overlay = null;
    }
  }

  dispose(): void {
    this.disposeOverlay();
    super.dispose();
  }
}
